import { type ThunkAction, type UnknownAction } from "@reduxjs/toolkit";

import type { RootState } from "@/app/store";
import { PaymentsWalletsService } from "@/modules/dashboard/wallets/services/paymentsWallets.service";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { normalizePaymentError } from "@/shared/services/payments/paymentErrors";
import { UserType } from "@/shared/types/user.entity";
import { trimTrailingZeros } from "@/shared/utils/decimal";

import { PaymentsQuotesService } from "../../dispersions/services/paymentsQuotes.service";
import { newIdempotencyKey, SwapService } from "../services/swap.service";
import {
  setFormSubmitError,
  setFormSubmitting,
  setIdempotencyKey,
  setLastChangedAmountField,
  setQuoteData,
  setQuoteError,
  setQuoteLoading,
  setSwapSuccess,
  setWallets,
  setWalletsAccessDenied,
  setWalletsError,
  setWalletsLoading,
  updateFormField,
} from "./swapsSlice";

/** One page is plenty — an organization has at most a handful of currency wallets. */
const WALLETS_PAGE_LIMIT = 200;

/** Balances only change when this screen moves them, and a swap refreshes them explicitly. */
const WALLETS_CACHE_MS = 5 * 60 * 1000;

/**
 * INTERNAL staff act on a client organization and must name it; members are pinned server-side from the JWT
 * and must NOT send it (sending another org's id is a 403).
 */
function organizationIdParam(state: RootState): string | undefined {
  const user = state.auth.user;
  if (user?.user_type !== UserType.INTERNAL) return undefined;
  return user.organization_id ?? user.organization?.id;
}

/**
 * Activated wallets + the activation catalog, in one pass. The catalog is what lets the destination selector
 * offer a currency the organization does not hold yet: the backend creates that wallet when the swap is
 * redeemed.
 */
export const thunkFetchWallets =
  (force = false): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    const { wallets } = getState().swaps;
    // Single-flight, plus the access latch.
    if (wallets.isLoading || !wallets.hasAccess) return;
    // Serve from cache unless the caller needs fresh numbers (i.e. right after a swap moved them). Without
    // this, any remount of a consumer re-hits the API for data that cannot have changed.
    const isFresh = wallets.lastFetched !== null && Date.now() - wallets.lastFetched < WALLETS_CACHE_MS;
    if (!force && isFresh) return;

    dispatch(setWalletsLoading(true));
    try {
      const organizationId = organizationIdParam(getState());
      const [walletsRes, currenciesRes] = await Promise.all([
        PaymentsWalletsService.list({
          limit: WALLETS_PAGE_LIMIT,
          ...(organizationId ? { organizationId } : {}),
        }),
        PaymentsWalletsService.listCurrencies(),
      ]);
      dispatch(
        setWallets({
          wallets: walletsRes.data.data,
          availableCurrencies: currenciesRes.data.currencies,
        }),
      );
    } catch (error: unknown) {
      // A missing `payments:read` grant latches for the session — the page renders NoAccessView instead of
      // retrying on every mount (each denial costs a silent /auth/refresh + replay).
      if (HttpApiService.isAccessDeniedError(error)) {
        dispatch(setWalletsAccessDenied());
        return;
      }
      dispatch(setWalletsError(HttpApiService.getErrorMessage(error)));
    }
  };

export interface SwapQuoteParams {
  originCurrency: string;
  destinationCurrency: string;
  /** Exactly one of the two — the other side is derived by the pricing engine. */
  sendAmount?: string;
  receiveAmount?: string;
}

/**
 * Price the pair with `service: "swap"`. The response is a persisted, single-use quote; the swap redeems it
 * by id, so nothing here moves money.
 */
export const thunkFetchQuote =
  (params: SwapQuoteParams): ThunkAction<Promise<void>, RootState, unknown, UnknownAction> =>
  async (dispatch, getState) => {
    dispatch(setQuoteLoading(true));
    dispatch(setLastChangedAmountField(params.sendAmount != null ? "origin" : "destination"));

    try {
      const organizationId = organizationIdParam(getState());
      const { data } = await PaymentsQuotesService.createQuote({
        origin_currency: params.originCurrency,
        destination_currency: params.destinationCurrency,
        service: "swap",
        ...(params.sendAmount != null ? { send_amount: params.sendAmount } : {}),
        ...(params.receiveAmount != null ? { receive_amount: params.receiveAmount } : {}),
        ...(organizationId ? { organization_id: organizationId } : {}),
      });

      dispatch(setQuoteData(data.quote));
      // Mirror the derived side back into the form so both inputs always show the quoted amounts.
      if (params.sendAmount != null) {
        const value = trimTrailingZeros(data.quote.amounts.receive);
        dispatch(updateFormField({ field: "amount_destination", value }));
      } else {
        const value = trimTrailingZeros(data.quote.amounts.send);
        dispatch(updateFormField({ field: "amount_origin", value }));
      }
    } catch (error: unknown) {
      dispatch(setQuoteError(HttpApiService.getErrorMessage(error)));
    }
  };

/**
 * Redeem the active quote. Resolves `true` when the swap completed, so the caller can react without reading
 * the store back.
 *
 * The idempotency key is minted once per attempt and kept in the store: a retry after a network error reuses
 * it and replays the original swap, whereas a fresh key on the same quote would be rejected as already
 * redeemed. It is cleared on success and on any terminal (non-network) failure.
 */
export const thunkSubmitSwap =
  (): ThunkAction<Promise<boolean>, RootState, unknown, UnknownAction> => async (dispatch, getState) => {
    const state = getState();
    const quote = state.swaps.quote.data;
    if (!quote) return false;

    const idempotencyKey = state.swaps.form.idempotencyKey ?? newIdempotencyKey();
    dispatch(setIdempotencyKey(idempotencyKey));
    dispatch(setFormSubmitting(true));
    dispatch(setFormSubmitError(null));

    // Read the pre-swap balances now; the response carries the post-swap ones.
    const walletsByCurrency = state.swaps.wallets.byCurrency;
    const oldBalances = {
      origin: walletsByCurrency[quote.currency.from]?.balance_available ?? null,
      destination: walletsByCurrency[quote.currency.to]?.balance_available ?? null,
    };

    try {
      const organizationId = organizationIdParam(state);
      const { data } = await SwapService.createSwap(
        {
          quote: quote.id,
          ...(organizationId ? { organization_id: organizationId } : {}),
        },
        idempotencyKey,
      );

      dispatch(
        setSwapSuccess({
          oldBalances,
          newBalances: data.swap.wallets,
          summary: {
            origin_currency: data.swap.summary.origin_currency,
            destination_currency: data.swap.summary.destination_currency,
            total_debited: data.swap.summary.total_debited,
            amount_received: data.swap.summary.amount_received,
            effective_rate: data.swap.summary.effective_rate,
          },
        }),
      );
      dispatch(setIdempotencyKey(null));
      // Forced: the swap just moved these balances, so the cache is known-stale.
      await dispatch(thunkFetchWallets(true));
      return true;
    } catch (error: unknown) {
      // Through the normaliser so a `ValidationPipe` rejection renders as sentences rather than an array
      // concatenated with no separator, and so an expired quote is named as such instead of surfacing
      // whichever 400 the server happened to reach first.
      const normalized = normalizePaymentError(error, { quoteExpiresAt: quote?.expires_at });
      dispatch(setFormSubmitError(normalized.message));
      // The server answered, so this attempt is settled: a new attempt needs a new key (and a new quote,
      // since the old one may have been consumed).
      if (HttpApiService.isAxiosError(error) && error.response) {
        dispatch(setIdempotencyKey(null));
      }
      return false;
    } finally {
      dispatch(setFormSubmitting(false));
    }
  };
